import { AxiosError } from 'axios';
import { castArray, has, isEmpty, some, values } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { appConfigsApi } from 'api';
import {
  CloseFormikDialogResult,
  DataWrapper,
  DialogProps,
  FormikDialog,
} from 'components';
import {
  AppConfigArea,
  AppConfigKey,
  P2PProviderType,
  PayoutRequisitesAutomationType,
  QueryKey,
} from 'enums';
import {
  useMutation,
  usePrevious,
  useUserContext,
  useP2PProvidersConfig,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AppConfig, AppConfigSchema } from 'types';
import { formatUtils, validationUtils } from 'utils';

import { ConfigForm } from './ConfigForm';

type Props = DialogProps<Partial<AppConfig>>;

type Values = {
  key: AppConfigKey;
  p2pProviderType?: P2PProviderType;
  bankId?: string;
  payoutRequisitesAutomationType?: PayoutRequisitesAutomationType;
  jsonObjectValue?: string;
  jsonArrayValue?: string;
};

export const ConfigDetailsDialog: React.FC<Props> = ({
  data,
  open,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.configs',
  });
  const { t: tCommon } = useTranslation();

  const queryClient = useQueryClient();
  const { banks } = useUserContext();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const prevOpen = usePrevious(open);

  const isNew = useMemo(() => !data?.id, [data]);
  const isPayinP2PProvider = useMemo(
    () => data?.area === AppConfigArea.PayinP2PProvider,
    [data?.area],
  );
  const isPayoutP2PProvider = useMemo(
    () => data?.area === AppConfigArea.PayoutP2PProvider,
    [data?.area],
  );
  const isTron = useMemo(() => data?.area === AppConfigArea.Tron, [data?.area]);
  const isP2PProvider = useMemo(
    () => isPayinP2PProvider || isPayoutP2PProvider,
    [isPayinP2PProvider, isPayoutP2PProvider],
  );
  const isPayoutRequisitesAutomation = useMemo(
    () => data?.area === AppConfigArea.PayoutRequisitesAutomation,
    [data],
  );

  const schemasQueryResult = useQuery(
    QueryKey.ConfigsSchemas,
    appConfigsApi.getAreaSchemas(data?.area!),
    {
      enabled: !!data && isNew,
    },
  );

  const configSchemaQueryResult = useQuery(
    [QueryKey.ConfigsSchemas, data?.id],
    appConfigsApi.getConfigSchema(data?.id!),
    {
      onSuccess: (data) => {
        setSelectedKey(data.key);
      },
      enabled: !isNew,
    },
  );

  const queryResult = useMemo(
    () => (isNew ? schemasQueryResult : configSchemaQueryResult),
    [isNew, schemasQueryResult, configSchemaQueryResult],
  );

  const { isP2PProviderAvailable } = useP2PProvidersConfig();

  const schemas: AppConfigSchema[] = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }

    return castArray(queryResult.data).filter(
      (config) =>
        !isP2PProvider || isP2PProviderAvailable(config.p2pProviderType),
    );
  }, [queryResult.data, isP2PProvider, isP2PProviderAvailable]);

  const isSchemaWithBankId = useMemo(
    () =>
      some(
        schemas,
        (schema) => schema.key === selectedKey && has(schema, 'bankId'),
      ),
    [schemas, selectedKey],
  );

  const title = useMemo(
    () =>
      isNew ? t('details_dialog.create_title') : t('details_dialog.edit_title'),
    [t, isNew],
  );

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.Configs);
  }, [queryClient]);

  const createMutation = useMutation(appConfigsApi.create, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const updateMutation = useMutation(appConfigsApi.update, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const defaultInitialValues: Values = useMemo(
    () => ({
      key: '' as AppConfigKey,
      jsonObjectValue: '',
      jsonArrayValue: '',
    }),
    [],
  );

  const [initialValues, setInitialValues] =
    useState<Values>(defaultInitialValues);

  const validationSchema: Yup.ObjectSchema<Partial<Values>> = useMemo(() => {
    let p2pProviderTypeSchema = Yup.string().oneOf(values(P2PProviderType));
    if (isP2PProvider) {
      p2pProviderTypeSchema = p2pProviderTypeSchema.required(
        tCommon('errors.required'),
      );
    }

    let payoutRequisitesAutomationTypeSchema = Yup.string().oneOf(
      values(PayoutRequisitesAutomationType),
    );
    if (isPayoutRequisitesAutomation) {
      payoutRequisitesAutomationTypeSchema =
        payoutRequisitesAutomationTypeSchema.required(
          tCommon('errors.required'),
        );
    }

    return Yup.object().shape({
      key: Yup.string()
        .oneOf(values(AppConfigKey))
        .required(tCommon('errors.required')),
      p2pProviderType: p2pProviderTypeSchema,
      bankId: Yup.lazy(() =>
        isSchemaWithBankId
          ? Yup.string().required(tCommon('errors.required'))
          : Yup.string(),
      ),
      payoutRequisitesAutomationType: payoutRequisitesAutomationTypeSchema,
      jsonObjectValue: Yup.string(),
      jsonArrayValue: Yup.string(),
    });
  }, [
    tCommon,
    isSchemaWithBankId,
    isP2PProvider,
    isPayoutRequisitesAutomation,
  ]);

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok || !result.data) {
        setInitialValues(defaultInitialValues);
        return onClose({ ok: false });
      }
      const options = {
        onSuccess: () => {
          setInitialValues(defaultInitialValues);
          onClose({ ok: true });
        },
        onError: (error: AxiosError) => {
          result.data?.formikHelpers.setErrors(
            validationUtils.getFormErrors(error),
          );
        },
      };
      let jsonObjectValue = null;
      let jsonArrayValue = null;
      if (result.data.values?.jsonObjectValue) {
        const parsedJson = formatUtils.parseJsonText({
          text: result.data.values.jsonObjectValue,
        });
        if (parsedJson.isJson) {
          jsonObjectValue = JSON.parse(parsedJson.parsedText);
        }
      }
      if (result.data.values?.jsonArrayValue) {
        const parsedJson = formatUtils.parseJsonText({
          text: result.data.values.jsonArrayValue,
        });
        if (parsedJson.isJson) {
          jsonArrayValue = JSON.parse(parsedJson.parsedText);
        }
      }
      if (isNew) {
        createMutation.mutate(
          {
            ...result.data.values,
            jsonObjectValue,
            jsonArrayValue,
            area: data?.area,
          },
          options,
        );
      } else {
        updateMutation.mutate(
          {
            id: data?.id!,
            data: {
              jsonObjectValue,
              jsonArrayValue,
            },
          },
          options,
        );
      }
    },
    [
      isNew,
      defaultInitialValues,
      onClose,
      createMutation,
      data?.area,
      data?.id,
      updateMutation,
    ],
  );

  useEffect(() => {
    if (open && !prevOpen) {
      setInitialValues(
        data?.id
          ? {
              key: data.key!,
              jsonObjectValue: JSON.stringify(data.jsonObjectValue, null, 2),
              jsonArrayValue: JSON.stringify(data.jsonArrayValue, null, 2),
              ...(isP2PProvider &&
                isP2PProviderAvailable(data.p2pProviderType) && {
                  p2pProviderType: data.p2pProviderType,
                }),
              ...(data.bankId && {
                bankId: data.bankId,
              }),
              ...(isPayoutRequisitesAutomation && {
                payoutRequisitesAutomationType:
                  data.payoutRequisitesAutomationType,
              }),
            }
          : defaultInitialValues,
      );
    }
  }, [
    data,
    isTron,
    isP2PProvider,
    isPayoutRequisitesAutomation,
    defaultInitialValues,
    open,
    prevOpen,
    isP2PProviderAvailable,
  ]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose: handleClose,
      }}
      formikProps={{
        initialValues,
        validateOnMount: true,
        validationSchema,
      }}
    >
      {() => (
        <DataWrapper
          isLoading={
            queryResult.isLoading ||
            (!isNew && configSchemaQueryResult.isLoading)
          }
          isEmpty={isEmpty(schemas)}
        >
          <ConfigForm
            banks={banks}
            data={data}
            schemas={schemas}
            onKeyChange={setSelectedKey}
          />
        </DataWrapper>
      )}
    </FormikDialog>
  );
};
